import React, { useEffect, useState } from 'react';

import { TickedCheckboxPortal, UntickedCheckboxPortal, InfoImage } from 'src/assets/images';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import TealiumEvents from 'src/helpers/tealium-events';
import { InfoTray, DotLoader, Tooltip } from 'src/components';
import { Colors, bcaContractTermsAndConditionsUrl } from 'src/common';
import { Offer } from 'src/context/offer-context';
import './accept-contract-modal.scss';

const tooltipContent = () => (
  <>
    <span className="is-icon-6 icon-cross is-pulled-right"></span>
    <div className="tooltip-text">
      <p>
        The percentage selected will be what we deduct from your daily turnover to repay your
        advance. This percentage deduction will remain the same until your advance is fully paid
        off. This is automatically completed before your daily settlement into your business
        account.
      </p>
      <br />
      <p>Remember, when you don't transact, you don't pay.</p>
    </div>
  </>
);

type AcceptContractHeadProps = {
  disabled: boolean;
  onHide: () => void;
};

const AcceptContractHead = ({ disabled, onHide }: AcceptContractHeadProps) => (
  <>
    <h1 className="text-3xl font-medium text-grey-600 contract-header is-hidden-mobile">
      Business Cash Advance contract
    </h1>

    <p className="contract-disclaimer">
      By selecting "Confirm", you agree to the Business Cash Advance Sale & Vodacom terms and
      conditions
    </p>

    <button
      className="close-button icon-cross is-icon-4 is-pulled-right is-hidden-mobile"
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onHide();
        }
      }}
    />
  </>
);

type AcceptContractBodyProps = {
  currentOffer?: Offer;
  className?: string;
  earlySettlementAmount?: number;
};

export const AcceptContractBody = ({
  currentOffer,
  className,
  earlySettlementAmount,
}: AcceptContractBodyProps) => (
  <div className="contract-info">
    <table className={`table is-fullwidth ${className}`}>
      <tbody>
        <tr>
          <td>Funding amount</td>
          <td className="contract-info-values" align="right">
            {`R ${formatCurrencyAmountSimple(currentOffer?.Amount)}`}
          </td>
        </tr>
        <tr>
          <td>Repayment frequency</td>
          <td className="contract-info-values" align="right">
            Daily
          </td>
        </tr>
        <tr>
          <td>Approximate repayment term</td>
          <td className="contract-info-values" align="right">
            {`${currentOffer?.Term ?? ''} Months`}
          </td>
        </tr>
        <tr>
          <td className="flex gap-2">
            Repayment rate
            <Tooltip showOnHover isMobile={false} lightMode content={tooltipContent()}>
              <img className="w-5 h-5 cursor-pointer" src={InfoImage} />
            </Tooltip>
          </td>
          <td className="contract-info-values" align="right">
            {`${currentOffer?.Rate ?? ''} %`}
          </td>
        </tr>
        <tr>
          <td>Cost of funding</td>
          <td className="contract-info-values" align="right">
            {`R ${formatCurrencyAmountSimple(
              (currentOffer?.TotalRepayments ?? currentOffer?.TotalRepayment ?? 0) -
                (currentOffer?.Amount ?? 0),
            )}`}
          </td>
        </tr>
        <tr>
          <td>Total repayment</td>
          <td className="contract-info-values" align="right">
            {`R ${formatCurrencyAmountSimple(currentOffer?.TotalRepayment)}`}
          </td>
        </tr>
        {!!earlySettlementAmount && (
          <tr>
            <td>Current early settlement amount</td>
            <td className="contract-info-values" align="right">
              R {formatCurrencyAmountSimple(earlySettlementAmount)}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

type AcceptContractFootProps = {
  accepted: boolean;
  setAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  disabled: boolean;
  onAcceptOffer: () => void;
};

const AcceptContractFoot = ({
  accepted,
  setAccepted,
  disabled,
  onAcceptOffer,
}: AcceptContractFootProps) => (
  <>
    <div className="accept-checkbox">
      <img
        src={accepted ? TickedCheckboxPortal : UntickedCheckboxPortal}
        alt="toggle-icon"
        onClick={() => setAccepted(!accepted)}
      />
      <p className="mr-1 text-grey-500">I have read and agree to the </p>
      <a
        className="text-red-600 underline"
        href={bcaContractTermsAndConditionsUrl}
        target="_blank"
        rel="noreferrer"
      >
        terms and conditions.
      </a>
    </div>
    <div className="accept-button-container">
      <button
        className="button is-fullwidth accept-button"
        onClick={() => {
          onAcceptOffer();
        }}
        automationid="BCA_Button_Close_Modal"
        disabled={disabled || !accepted}
      >
        {disabled ? <DotLoader color={Colors.BlueGray4} /> : 'Confirm'}
      </button>
    </div>
  </>
);

type AcceptContractModalProps = {
  show: boolean;
  onHide: () => void;
  onAcceptOffer: () => void;
  currentOffer?: Offer;
  disabled: boolean;
};

export default function AcceptContractModal({
  show,
  onHide,
  onAcceptOffer,
  currentOffer,
  disabled,
}: AcceptContractModalProps) {
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (show) {
      TealiumEvents.confirmTCsBCAPageLoad();
    }
  }, [show]);

  return (
    <div className="AcceptContractModal">
      <div className={`modal ${show ? 'is-active' : ''} is-hidden-mobile`}>
        <div className="modal-background"></div>
        <div className="modal-card">
          <header className="container modal-card-head has-text-centered">
            <AcceptContractHead disabled={disabled} onHide={onHide} />
          </header>
          <section className="modal-card-body">
            <AcceptContractBody currentOffer={currentOffer} />
          </section>
          <footer className="modal-card-foot">
            <AcceptContractFoot
              accepted={accepted}
              setAccepted={setAccepted}
              onAcceptOffer={onAcceptOffer}
              disabled={disabled}
            />
          </footer>
        </div>
      </div>
      <InfoTray
        selected={show}
        setSelected={() => onHide()}
        title="Business Cash Advance contract"
        isFullscreen={true}
        className="is-hidden-tablet"
      >
        <p className="top-mobile-page-title navbar-title-mobile is-hidden-tablet">Get Financed</p>
        <AcceptContractHead disabled={disabled} onHide={onHide} />
        <AcceptContractBody currentOffer={currentOffer} />
        <AcceptContractFoot
          accepted={accepted}
          setAccepted={setAccepted}
          onAcceptOffer={onAcceptOffer}
          disabled={disabled}
        />
      </InfoTray>
    </div>
  );
}
